import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Modal, Form, Spin } from 'antd';
import { FaRegTrashAlt, FaEdit } from 'react-icons/fa';
import { PlusOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const Moeda = () => {
  const [moedas, setMoedas] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [selectedMoeda, setSelectedMoeda] = useState(null);
  const [moedaToDelete, setMoedaToDelete] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const total = filteredData.length;
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, total);

  useEffect(() => {
    document.title = 'Moedas';
    fetchMoedas();
  }, []);

  const sms_error = (msg) => toast.error(msg);
  const sms_success = (msg) => toast.success(msg);

  const fetchMoedas = async () => {
    setLoading(true);
    try {
      const response = await Api.ListMoeda();
      const dados = Array.isArray(response) ? response : [];

      const formatado = dados.map(item => ({
        key: item.id,
        ...item,
      }));

      setMoedas(formatado);
      setFilteredData(formatado);
    } catch (error) {
      sms_error('Erro ao buscar moedas: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

const handleSearch = (value) => {
  setSearchText(value);
  setCurrentPage(1);

  const search = (value || '').toString().toLowerCase();

  const filtered = moedas.filter(item => {
    if (!item) return false;

    return (
      (item.descricao || '').toLowerCase().includes(search) ||
      (item.moeda || '').toLowerCase().includes(search) ||
      (item.moedaplural || '').toLowerCase().includes(search) ||
      (item.unidade || '').toLowerCase().includes(search) ||
      (item.unidadeplural || '').toLowerCase().includes(search) ||
      (item.sigla || '').toLowerCase().includes(search) ||
      (item.simbolo || '').toLowerCase().includes(search)
    );
  });

  setFilteredData(filtered);
};


  const handleAdd = async (values) => {
    setAddLoading(true);
    try {
      const result = await Api.AddMoeda(values);
      if (result.success) {
        sms_success('Moeda adicionada com sucesso!');
        setIsAddModalVisible(false);
        form.resetFields();
        await fetchMoedas();
      } else {
        sms_error(result.message || 'Erro ao adicionar moeda');
      }
    } catch (error) {
      sms_error('Erro ao adicionar moeda: ' + error.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
      const result = await Api.AlterMoeda({
        id: selectedMoeda.key,
        ...values,
      });
      if (result.success) {
        sms_success('Moeda editada com sucesso!');
        setIsEditModalVisible(false);
        editForm.resetFields();
        await fetchMoedas();
      } else {
        sms_error(result.message || 'Erro ao editar moeda');
      }
    } catch (error) {
      sms_error('Erro ao editar moeda: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const result = await Api.DeleteMoeda({ id: moedaToDelete });
      if (result.success) {
        sms_success('Moeda excluída com sucesso!');
        await fetchMoedas();
      } else {
        sms_error(result.message || 'Erro ao excluir moeda');
      }
    } catch (error) {
      sms_error('Erro ao excluir moeda: ' + error.message);
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  const renderSearchableColumn = (dataIndex, label) => ({
    title: label,
    dataIndex,
    key: dataIndex,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Pesquisar ${label}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            handleSearch(selectedKeys[0]);
          }}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => {
            confirm();
            handleSearch(selectedKeys[0]);
          }}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Filtrar
        </Button>
        <Button onClick={() => {
          clearFilters();
          handleSearch('');
        }} size="small" style={{ width: 90 }}>
          Limpar
        </Button>
      </div>
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
  });

  const columns = [
    renderSearchableColumn('descricao', 'Descrição'),
    renderSearchableColumn('moeda', 'Moeda'),
    renderSearchableColumn('moedaplural', 'Plural'),
    renderSearchableColumn('unidade', 'Unidade'),
    renderSearchableColumn('unidadeplural', 'Plural Unidade'),
    renderSearchableColumn('sigla', 'Sigla'),
    renderSearchableColumn('simbolo', 'Símbolo'),
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 10 }}>
          <FaEdit
            style={{ cursor: 'pointer', color: '#1890ff' }}
            onClick={() => {
              setSelectedMoeda(record);
              editForm.setFieldsValue(record);
              setIsEditModalVisible(true);
            }}
          />
          <FaRegTrashAlt
            style={{ cursor: 'pointer', color: '#ff4d4f' }}
            onClick={() => {
              setMoedaToDelete(record.key);
              setIsDeleteModalVisible(true);
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div>
      <ToastContainer />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => setIsAddModalVisible(true)}
        >
          Adicionar
        </Button>
      </div>

      <Spin spinning={loading} tip="Carregando...">
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredData.length,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
          }}
          scroll={{ x: 'max-content' }}
        />
      </Spin>

      <div style={{ marginBottom: 10 }}>
        <strong>
          {total > 0
            ? `Mostrando ${startIndex}–${endIndex} de ${total} moedas`
            : 'Nenhuma moeda encontrada'}
        </strong>
      </div>

      {/* Modal Adicionar */}
      <Modal
        title="Adicionar Moeda"
        visible={isAddModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsAddModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
            <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Descrição é obrigatória'  }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="moeda" label="Moeda" rules={[{ required: true, message: 'Moeda é obrigatória' }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="moedaplural" label="Plural Moeda" rules={[{ required: true, message: 'Plural Moeda é obrigatória' }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="unidade" label="Unidade" rules={[{ required: true, message: 'Unidade é obrigatório'  }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="unidadeplural" label="Plural Unidade" rules={[{ required: true, message: 'Plural Unidade é obrigatório' }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="sigla" label="Sigla" rules={[{ required: true, message: 'Sigla é obrigatória'  }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="simbolo" label="Símbolo" rules={[{ required: true, message: 'Símbolo é obrigatório'  }]}>
            <Input  maxLength={50}/>
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsAddModalVisible(false)} style={{ marginRight: 8 }}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={addLoading}>
                Salvar
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar */}
      <Modal
        title="Editar Moeda"
        visible={isEditModalVisible}
        onCancel={() => {
          editForm.resetFields();
          setIsEditModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Descrição é obrigatória'  }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="moeda" label="Moeda" rules={[{ required: true, message: 'Moeda é obrigatória' }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="moedaplural" label="Plural Moeda" rules={[{ required: true, message: 'Plural Moeda é obrigatória' }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="unidade" label="Unidade" rules={[{ required: true, message: 'Unidade é obrigatório'  }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="unidadeplural" label="Plural Unidade" rules={[{ required: true, message: 'Plural Unidade é obrigatório' }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="sigla" label="Sigla" rules={[{ required: true, message: 'Sigla é obrigatória'  }]}>
            <Input  maxLength={50}/>
          </Form.Item>
          <Form.Item name="simbolo" label="Símbolo" rules={[{ required: true, message: 'Símbolo é obrigatório'  }]}>
            <Input  maxLength={50}/>
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsEditModalVisible(false)} style={{ marginRight: 8 }}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                Salvar
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Exclusão */}
      <Modal
        title="Confirmar Exclusão"
        visible={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Sim"
        cancelText="Não"
        confirmLoading={deleteLoading}
      >
        <p>Tem certeza que deseja excluir esta moeda?</p>
      </Modal>
    </div>
  );
};

export default Moeda;
